import React from "react";
import useMobileBreakpoint from "@arctheme-hooks/useMobileBreakpoint";
import { Paragraph } from "@project-stories/elements/Paragraph/Paragraph";
import { Image } from "@project-stories/elements/Image/Image";
import "./BlogArticleTile.scss";

export const BlogArticleTile = ({
                                    article,
                                    show_excerpt = false,
                                    settings,
                                    isFeatured = false,
                                    showContent = true,
                                    showCategory = false,
                                    showMetadata = false,
                                    imageRounding = "none"
                                }) => {
    const isMobile = useMobileBreakpoint(settings.mobile_breakpoint);

    const charLimit = 90;
    const truncateText = (text, charLimit) => {
        return text.length <= charLimit ? text : text.substring(0, charLimit) + "...";
    };

    // Calculate read time based on article content character count
    const calculateReadTime = (contentSize) => {
        if (!contentSize || contentSize === 0) return "5 min read";
        
        // Convert character count to estimated word count
        // Average word length in English is about 5.1 characters including spaces
        const avgCharsPerWord = 12;
        const estimatedWordCount = Math.ceil(contentSize / avgCharsPerWord);
        
        // Average reading speed is about 200-250 words per minute
        const wordsPerMinute = 250;
        const readTimeMinutes = Math.ceil(estimatedWordCount / wordsPerMinute);
        
        // Ensure minimum 1 minute read time
        const finalReadTime = Math.max(1, readTimeMinutes);
        
        return `${finalReadTime} min read`;
    };

    const getArticleUrl = () => {
        let articleUrl = `/blogs/${article.handle}`;
        if (article.blog?.handle && !article.handle.includes(article.blog.handle)) {
            articleUrl = `/blogs/${article.blog.handle}/${article.handle}`;
        } else if (settings?.blog?.handle && !article.handle.includes(settings.blog.handle)) {
            articleUrl = `/blogs/${settings.blog.handle}/${article.handle}`;
        }
        return articleUrl;
    };

    const article_url = getArticleUrl();

    let category = "";
    if (showCategory && article?.tags?.length) {
        const tag = article.tags.find((item) => item.includes("category:"));
        if (tag) category = tag.split("category:")[1];
    }

    // Get read time - either from metafields or calculate from content size
    const readTime = article?.metafields?.read_time || article?.content_size 
        ? calculateReadTime(parseInt(article.content_size))
        : "5 min read";
    
    const dosageInfo = article?.metafields?.dosage_info || "";
    
    // Get article title style from settings
    const titleStyle = settings?.section?.article_title_style || "h6";
    // Get article date and show settings
    const showArticleDate = settings?.section?.show_article_date || false;
  
    const articleDate = article?.published_at || "";
    const metadataDelimiter = settings?.section?.metadata_delimiter || "•";
    
    // Get read more button settings
    const showReadMoreButton = settings?.section?.show_read_more_button || false;
    const readMoreText = settings?.section?.read_more_text || "Read More";
    const readMoreStyle = settings?.section?.read_more_button_style || "button-tertiary";
    
    // Determine image rounding class based on prop
    const imageRoundingClass = imageRounding === "rounded" ? "rounded-lg" : 
                              imageRounding === "full" ? "rounded-full" : "";

    return (
        <div className="blog-article-tile">
            <a href={article_url} className="blog-article-tile__image-container">
                <Image
                    imageUrl={typeof article.image === "object" && article.image.src ? article.image.src : article.image}
                    classes={`blog-article-tile__image h-auto ${imageRoundingClass}`}
                    width={article.image.width || 800}
                    height={article.image.height || 600}
                    alt={article.title || "Blog article image"}
                />
            </a>

            <div className="blog-article-tile__details">
                <div className="blog-article-tile__text-group">
                    {!showMetadata && showCategory && category && <Paragraph text={category} type="blog-article-tile__category" />}

                    <div className="blog-article-tile__header">
                      <a href={article_url}>
                        <Paragraph
                            text={article.title}
                            type={`blog-article-tile__title ${titleStyle}`}
                        />
                      </a>
                    </div>

                    {showMetadata && (
                        <div className="blog-article-tile__metadata">
                            {showArticleDate && articleDate && (
                                <span className="blog-article-tile__date">{articleDate}</span>
                            )}
                            {showArticleDate && articleDate && (readTime || dosageInfo || category) && (
                                <span className="blog-article-tile__separator">{metadataDelimiter}</span>
                            )}
                            {readTime && <span className="blog-article-tile__read-time">{readTime}</span>}
                            {dosageInfo && <span className="blog-article-tile__dosage-info">{dosageInfo}</span>}
                            {(readTime && (dosageInfo || category)) && <span className="blog-article-tile__separator">{metadataDelimiter}</span>}
                            {showCategory && category && <span className="blog-article-tile__category">{category}</span>}
                        </div>
                    )}
                </div>

                {showContent && (
                    <div className="blog-article-tile__content">
                        <Paragraph text={show_excerpt ? truncateText(article.excerpt || "", charLimit) : ""} />
                    </div>
                )}
                
                {showReadMoreButton && (
                    <div className="blog-article-tile__read-more-button">
                        <a href={article_url} className={`button ${readMoreStyle}`}>
                            {readMoreText}
                        </a>
                    </div>
                )}
            </div>
        </div>
    );
};

export default BlogArticleTile;
